import { useMemo, useEffect } from 'react';
import { usePersistentState } from '@hooks/usePersistentState';
import { useCxGlobalSettings } from '@hooks/useCallExperience';
import { Highlighted, HeardText, OriginBadge, describeGuard, heardValues, useCxPrefs } from './cxFormat';

const READBACK_KEY = 'ivrOrderReadback';
const ELIGIBLE_ITEMS_KEY = 'ivrEligibleItems';

const INPUT_LABELS = { digits: 'entry', timeout: 'no key', anyKey: 'any key' };

function Switch({ on, disabled, title, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      className={`cx-sw${on ? ' cx-sw-on' : ''}`}
      disabled={disabled}
      title={title}
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
    />
  );
}

/** "⚑ …" chips for what taking a branch or giving an answer does (an order, a task, a contact change). */
function Effects({ items, answer }) {
  if (!items?.length) return null;
  return items.map((text) => (
    <span key={text} className="cx-effect" title="What happens">⚑ {answer ? `Answer ${answer}: ` : ''}{text}</span>
  ));
}

const effectsFor = (prompt, answerEffects) => (answerEffects || [])
  .filter((e) => (e.identifier ? e.identifier === prompt.key : e.formLocation === prompt.group));

/** One spoken line: label, where its words come from, settings and the text. */
function PromptLine({ prompt, ctx, lang, conditions, heard, answerEffects, onOpen, onToggle, togglePending }) {
  const { technical, hear } = useCxPrefs();
  const silent = !prompt.text?.trim();
  const toggleTitle = ctx.isCompany
    ? 'Turn this question on or off for this company'
    : ctx.isParent ? 'Questions can only be turned off per company, not for a parent group'
      : 'On by default. View a company to turn it off.';
  return (
    <div
      className={`cx-say${prompt.enabled ? '' : ' cx-dim'}${prompt.coachOnly ? ' cx-say-coach' : ''}`}
      onClick={() => onOpen(prompt)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(prompt); }}
      role="button"
      tabIndex={0}
    >
      <span className="cx-say-ico" aria-hidden="true">{prompt.coachOnly ? '🎧' : '🔊'}</span>
      <div className="cx-say-main">
        <div className="cx-say-meta">
          <span className="cx-say-label">{prompt.label}</span>
          {prompt.when && (
            <span className="cx-when" title={technical ? `Spoken only when: ${prompt.when}` : 'Spoken only when'}>
              only when {describeGuard(prompt.when, conditions)}
            </span>
          )}
          <OriginBadge origin={prompt.origin} />
          {prompt.required && <span className="cx-badge cx-badge-req" title="Can't be turned off">required</span>}
          {prompt.toggleable && (
            <Switch on={prompt.enabled} disabled={!ctx.isCompany || togglePending} title={toggleTitle}
              onChange={(enabled) => onToggle(prompt.key, enabled)} />
          )}
          {prompt.customizedCount > 0 && <span className="cx-badge cx-badge-co">custom for {prompt.customizedCount}</span>}
          {prompt.disabledCount > 0 && <span className="cx-badge cx-badge-off">off for {prompt.disabledCount}</span>}
          {!prompt.enabled && <span className="cx-badge cx-badge-off">off for {ctx.name}</span>}
          {lang === 'es' && prompt.spanishMissing && !silent && (
            <span className="cx-badge cx-badge-warn" title="Spanish callers hear the English wording">no Spanish</span>
          )}
          {prompt.coachOnly && <span className="cx-when">coach hears</span>}
        </div>
        {silent
          ? <div className="cx-say-text cx-silent">Silent: {prompt.note || 'no wording set'}</div>
          : (
            <div className="cx-say-text">
              {hear && heard
                ? <HeardText text={prompt.text} values={prompt.key === READBACK_KEY ? heard.readback : heard.base} source={heard.source} />
                : <Highlighted text={prompt.text} />}
            </div>
          )}
        {hear && prompt.key === ELIGIBLE_ITEMS_KEY && heard?.itemsNote && <div className="cx-caption">{heard.itemsNote}</div>}
        {effectsFor(prompt, answerEffects).length > 0 && (
          <div className="cx-effects">
            {effectsFor(prompt, answerEffects).map((e) => <Effects key={`${e.answer}:${e.text}`} items={[e.text]} answer={e.answer} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Branch({ branch, stepTitles, conditions }) {
  const { technical } = useCxPrefs();
  const impossible = branch.guardValue === false;
  const undecided = branch.guardValue == null && branch.guardReason;
  const input = branch.input === 'digit'
    ? <span className="cx-key">{branch.value}</span>
    : INPUT_LABELS[branch.input] ? <span className="cx-key cx-key-wide">{INPUT_LABELS[branch.input]}</span> : null;
  return (
    <div className={`cx-br${impossible ? ' cx-br-dim' : ''}`}>
      {input}
      <span className="cx-br-label">{branch.label}</span>
      <span className="cx-br-target">→ {stepTitles[branch.target] || branch.target}</span>
      <Effects items={branch.effects} />
      {branch.guard && !impossible && (
        <span className="cx-when" title={technical ? `Condition: ${branch.guard}` : 'Condition'}>when {describeGuard(branch.guard, conditions)}</span>
      )}
      {impossible && <span className="cx-br-why">Doesn&apos;t apply: {branch.guardReason}</span>}
      {undecided && <span className="cx-br-why">{branch.guardReason}</span>}
    </div>
  );
}

/** Prompts in declared order, with expanded question groups under a subheading. */
function PromptList({ prompts, ...props }) {
  const blocks = [];
  prompts.forEach((p) => {
    const last = blocks[blocks.length - 1];
    if (p.group && last?.group === p.group) last.items.push(p);
    else blocks.push({ group: p.group, label: p.groupLabel, items: [p] });
  });
  return blocks.map((b, i) => (b.group ? (
    <div key={`${b.group}-${i}`} className="cx-sub">
      <div className="cx-subhead">{b.label}</div>
      {b.items.map((p) => <PromptLine key={`${p.kind}:${p.key}`} prompt={p} {...props} />)}
    </div>
  ) : b.items.map((p) => <PromptLine key={`${p.kind}:${p.key}:${p.when || ''}`} prompt={p} {...props} />)));
}

/** How long a step waits and what happens without a valid answer, in one sentence. */
function WaitNote({ step, stepTitles, options }) {
  const { technical } = useCxPrefs();
  const { gather, retry } = step;
  const seconds = gather.timeoutSeconds ?? options?.[gather.timeoutOption];
  const what = gather.numDigits === 1 ? 'a key' : `up to ${gather.numDigits} digits${gather.finishOnHash ? ' (ended with #)' : ''}`;
  const tries = retry && options?.[retry.maxAttemptsOption];
  return (
    <div className="cx-note">
      Waits {seconds != null ? `up to ${seconds} seconds` : 'a few seconds'} for {what}.
      {retry && <> If there&apos;s no valid answer it asks again ({tries != null ? `up to ${tries} tries` : 'a few times'}), then goes to <i>{stepTitles[retry.giveUpTarget] || retry.giveUpTarget}</i>.</>}
      {technical && (
        <span className="cx-muted"> (<code>IvrOptions.{gather.timeoutOption || 'fixed'}</code>{retry && <>, <code>{retry.maxAttemptsOption}</code></>})</span>
      )}
    </div>
  );
}

/** "Hear it": the question asked for each item when items are asked one at a time. */
function ItemQuestions({ supplies, preview }) {
  const { hear } = useCxPrefs();
  if (!hear || !supplies?.itemQuestions?.length) return null;
  const eligible = preview && new Set(preview.eligibleItems.map((i) => i.toLowerCase()));
  const questions = eligible ? supplies.itemQuestions.filter((q) => eligible.has(q.item.toLowerCase())) : supplies.itemQuestions;
  return (
    <div className="cx-sub">
      <div className="cx-subhead">{preview ? `Asked of ${preview.name}` : 'Asked for each eligible item'}</div>
      {preview && questions.length === 0 && <div className="cx-muted cx-small">Nothing is eligible, so no item is asked.</div>}
      {questions.map((q) => (
        <div key={q.item} className="cx-item-q">
          <b>{q.item}</b>: {q.text}
          {!q.fromQuestion && <span className="cx-badge cx-badge-gl" title="No supplies question for this item; the built-in wording is spoken">code default</span>}
        </div>
      ))}
      {!preview && <div className="cx-caption">Only the items the patient is eligible for are asked.</div>}
    </div>
  );
}

/** The skip that applies in this view (company settings, or the previewed patient), if any. */
export const appliedSkip = (step) => (step.skips || []).find((b) => b.guardValue === true);

function Step({ step, num, collapsed, onCollapse, stepTitles, ctx, onToggle, togglePending, options, ...props }) {
  const { technical } = useCxPrefs();
  const off = !step.enabled;
  const skipped = appliedSkip(step);
  return (
    <div id={`cx-step-${step.id}`} className={`cx-step${off || skipped ? ' cx-step-off' : ''}${collapsed ? ' cx-step-collapsed' : ''}`}>
      <div
        className="cx-shead"
        onClick={onCollapse}
        onKeyDown={(e) => { if (e.key === 'Enter') onCollapse(); }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
      >
        <span className={`cx-num${step.outcome ? ' cx-num-end' : ''}`}>{num}</span>
        <span className="cx-stitle">{step.title}</span>
        {step.toggle && (
          <>
            <Switch
              on={step.enabled}
              disabled={!ctx.isCompany || togglePending}
              title={ctx.isCompany ? 'Turn this step on or off for this company' : 'View a company to turn this step off'}
              onChange={(enabled) => onToggle(step.toggle, enabled)}
            />
            {technical && <code className="cx-muted">{step.toggle}</code>}
          </>
        )}
        {off && <span className="cx-offnote">Skipped for {ctx.name}: {step.offNote}</span>}
        {!off && skipped && (
          <span className="cx-offnote">Skipped for {props.preview ? props.preview.name : ctx.name}: {skipped.label} ({skipped.guardReason})</span>
        )}
        {!off && step.disabledCount > 0 && <span className="cx-badge cx-badge-off">off for {step.disabledCount}</span>}
        <span className="cx-caret" aria-hidden="true">▾</span>
      </div>
      {!collapsed && (
        <div className="cx-sbody">
          {step.note && <div className="cx-note">{step.note}</div>}
          {(step.skips || []).length > 0 && (
            <div className="cx-skips">
              <div className="cx-subhead">Not asked when</div>
              {step.skips.map((b, i) => <Branch key={i} branch={b} stepTitles={stepTitles} conditions={props.conditions} />)}
            </div>
          )}
          <PromptList prompts={step.prompts} ctx={ctx} onToggle={onToggle} togglePending={togglePending} answerEffects={step.answerEffects} {...props} />
          {step.repeat?.over === 'eligibleItems' && <ItemQuestions supplies={props.supplies} preview={props.preview} />}
          {step.gather && <WaitNote step={step} stepTitles={stepTitles} options={options} />}
          {step.repeat && <div className="cx-note">Asked once per item ({step.repeat.itemInputs.join(', ')}).</div>}
          {step.branches.length > 0 && (
            <div className="cx-branches">
              {step.branches.map((b, i) => <Branch key={i} branch={b} stepTitles={stepTitles} conditions={props.conditions} />)}
            </div>
          )}
          {step.outcome && <div className="cx-end">{step.outcome}</div>}
        </div>
      )}
    </div>
  );
}

/** A journey as numbered steps, with error and fallback paths collected at the end. */
export default function JourneyView({ journey, jumpTo, onJumped, ...rest }) {
  const { data: global } = useCxGlobalSettings();
  const heard = useMemo(() => {
    const line = journey.productLineId === 2 ? 'CGM' : 'CPAP';
    const preview = journey.preview;
    if (preview) {
      return {
        ...heardValues(journey, { companyName: rest.ctx?.name, items: preview.eligibleItemsSpoken || 'nothing' }),
        source: `From ${preview.name}'s record`,
        itemsNote: `${preview.name}'s eligible items right now.`,
      };
    }
    return {
      ...heardValues(journey, { companyName: rest.ctx?.isCompany ? rest.ctx.name : undefined }),
      source: 'Example value',
      itemsNote: journey.supplies
        ? `Lists the items this patient is eligible for. Shown here: every item the ${line} line can offer; the real list depends on the patient's insurance and order history.`
        : null,
    };
  }, [journey, rest.ctx]);
  const props = { ...rest, conditions: journey.conditions, options: global?.ivrOptions, heard, supplies: journey.supplies, preview: journey.preview };
  const [collapsed, setCollapsed] = usePersistentState(`cx.collapsed.${journey.id}:${journey.productLineId ?? ''}`, {});
  const stepTitles = useMemo(() => Object.fromEntries(journey.steps.map((s) => [s.id, s.title])), [journey]);
  const main = journey.steps.filter((s) => s.group !== 'system');
  const system = journey.steps.filter((s) => s.group === 'system');

  const setAll = (value) => setCollapsed(Object.fromEntries(journey.steps.map((s) => [s.id, value])));
  const flip = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  // "Call at a glance" asks to open a step and bring it into view.
  useEffect(() => {
    if (!jumpTo) return undefined;
    setCollapsed((c) => ({ ...c, [jumpTo.id]: false }));
    // Cleared once handled, so reopening the script later doesn't jump again.
    const t = setTimeout(() => {
      document.getElementById(`cx-step-${jumpTo.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onJumped?.();
    }, 50);
    return () => clearTimeout(t);
  }, [jumpTo, onJumped, setCollapsed]);

  return (
    <div className="cx-journey">
      <div className="cx-journey-tools">
        <button type="button" className="cx-btn cx-btn-small" onClick={() => setAll(false)}>Expand all</button>
        <button type="button" className="cx-btn cx-btn-small" onClick={() => setAll(true)}>Collapse all</button>
      </div>
      {main.map((step, i) => (
        <Step key={step.id} step={step} num={i + 1} collapsed={!!collapsed[step.id]} onCollapse={() => flip(step.id)}
          stepTitles={stepTitles} {...props} />
      ))}
      {system.length > 0 && (
        <>
          <h3 className="cx-section-title">Error and fallback paths</h3>
          {system.map((step) => (
            <Step key={step.id} step={step} num="!" collapsed={collapsed[step.id] ?? true} onCollapse={() => setCollapsed((c) => ({ ...c, [step.id]: !(c[step.id] ?? true) }))}
              stepTitles={stepTitles} {...props} />
          ))}
        </>
      )}
    </div>
  );
}
